import { useEffect, useState } from "react";
import {
    ListContainer,
    ListContext,
    PAGE_DEFAULT,
    PAGE_SIZE_UNLIMITED,
    Page,
    PageRoute,
    SearchBar,
    buildPath,
    buildTemplatePath,
    pn,
} from "@compass/core";
import { ListFooter } from "components";
import { RegionListDto, RegionListResource, ListFilterBase, Permission } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { RegionListPageHeader } from "./RegionListPageHeader";
import { RegionTable } from "./RegionTable";

const routeName = "regions";
export const regionListPageRoute: PageRoute = {
    handle: "Regions",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildPath(routeName),
};

export const RegionListPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const [listItems, setListItems] = useState<RegionListDto[]>([]);
    const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
        sortObjects: [{ property: pn<RegionListDto>("name"), isDesc: false }],
    });
    const [total, setTotal] = useState<number>();
    const [resource, setResource] = useState<RegionListResource>({});
    const [searchFocused, setSearchFocused] = useState<boolean>(false);

    const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
        listFilter: listFilter,
        setListFilter: setListFilter,
        fetchList: (listFilter) => {
            ApiService.regions.getList(listFilter).then((response) => {
                setListItems(response);
            });
        },
        fetchTotal: (listFilter) => {
            setTotal(undefined);
            ApiService.regions.getListTotal(listFilter).then((response) => {
                setTotal(response);
            });
        },
    });

    useEffect(() => {
        loadList(listFilter);
        ApiService.regions.getListResource().then((response) => {
            setResource(response);
        });
    }, []);

    return (
        <>
            <Page header={<RegionListPageHeader rootPermission={ROOT_PERMISSION} handle={regionListPageRoute.handle} />}>
                <ListContainer
                    fullHeight
                    header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
                    tableContainer={
                        <RegionTable
                            rootPermission={ROOT_PERMISSION}
                            tableContainerRef={tableContainerRef}
                            regions={listItems}
                            setRegions={setListItems}
                            resource={resource}
                            searchFocused={searchFocused}
                            sortObjects={listFilter.sortObjects}
                            sort={applySort}
                            loadList={() => loadList(listFilter)}
                        />
                    }
                    footer={
                        <ListFooter
                            total={total}
                            size={listFilter.pageSize}
                            page={listFilter.page}
                            onPageChange={applyPage}
                            onSizeChange={applyPageSize}
                            onSortMethodChange={applySortMethod}
                        />
                    }
                />
            </Page>
            <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
                <Outlet />
            </ListContext.Provider>
        </>
    );
};